import React, { useEffect, useMemo, useState } from "react";
import { Button, Segmented, Space, Tag } from "antd";
import {
  MailOutlined,
  PlusOutlined,
  UnorderedListOutlined,
  BarChartOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import dayjsTZ from "../../../utils/dayjs";
import { requestDataWithParams } from "../../../helpers/API/RequestFactory";
import { employeeRequests as employeeRequestsAction } from "../../../store/actions";
import PlanerFilters from "./components/PlanerFilters";
import PlanerMonthFilter from "./components/PlanerMonthFilter";
import PlanerGantt from "./components/PlanerGantt";
import PlanerListView from "./components/PlanerListView";
import PlanerCalendarView from "./components/PlanerCalendarView";
import PlanerOnLeaveSidebar from "./components/PlanerOnLeaveSidebar";
import PlanerDrawer from "./components/PlanerDrawer";
import InboxQueuePanel from "./components/InboxQueuePanel";
import RequestModal from "../../dashboard/AdminDashboard/AdminCards/WorkLeave/RequestModal/RequestModal";
import { STATUS_COLORS } from "./utils/statusColors";
import {
  filterLeaveByRange,
  monthRangeFromDayjs,
  yearRangeFromDayjs,
  userSubsWithLeaveInRange,
} from "./utils/leaveRange";
import {
  canManagePlaner,
  isPlanerEmployeeOnly,
  scopePlanerEmployees,
} from "./utils/planerAccess";
import { useAuth } from "../../../authentication/authHelpers";
import { canAccessGmailIntegration } from "../../../utils/gmailScope";
import "./PlanerPage.scss";

const VIEWS = {
  gantt: "gantt",
  list: "list",
  calendar: "calendar",
};

const PlanerPage = () => {
  const dispatch = useDispatch();
  const auth = useAuth();
  const canManage = canManagePlaner(auth);
  const employeeOnly = isPlanerEmployeeOnly(auth);
  const canUseGmailInbox = canManage && canAccessGmailIntegration(auth);
  const { employees } = useSelector((state) => state.employeesList);
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const { departments } = useSelector((state) => state.departments);
  const { isDarkMode } = useSelector((state) => state.usersList);

  // Login bootstrap only keeps 6 months; planner needs a wider history window.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await requestDataWithParams(
          "employeeRequests",
          dayjsTZ().subtract(2, "years").valueOf()
        );
        if (!cancelled && Array.isArray(res)) {
          dispatch(employeeRequestsAction(res));
        }
      } catch (err) {
        console.error("Planer leave history fetch failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch]);

  // Gantt canvas is wider than the viewport; lock document X-scroll while Planer is open.
  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    root.classList.add("planer-no-x-scroll");
    body.classList.add("planer-no-x-scroll");
    return () => {
      root.classList.remove("planer-no-x-scroll");
      body.classList.remove("planer-no-x-scroll");
    };
  }, []);

  const [department, setDepartment] = useState("");
  const [team, setTeam] = useState("");
  const [employeeSearch, setEmployeeSearch] = useState("");
  const [month, setMonth] = useState(() => dayjsTZ().startOf("month"));
  const [view, setView] = useState(
    employeeOnly ? VIEWS.calendar : VIEWS.gantt
  );
  const [selected, setSelected] = useState(null);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [requestModal, setRequestModal] = useState(false);
  const [editRequest, setEditRequest] = useState({});

  const { start: rangeStart, end: rangeEnd } = useMemo(
    () => monthRangeFromDayjs(month),
    [month]
  );

  const { start: yearStart, end: yearEnd, year: selectedYear } = useMemo(
    () => yearRangeFromDayjs(month),
    [month]
  );

  // Role-scoped roster before year/leave filters.
  const scopedEmployees = useMemo(
    () => scopePlanerEmployees(employees || [], auth),
    [employees, auth]
  );

  // Only employees who requested leave in the selected year appear in the planner.
  // Employees always see themselves (even with no leave yet).
  const yearLeaveSubs = useMemo(
    () =>
      userSubsWithLeaveInRange(employeeRequests || [], yearStart, yearEnd),
    [employeeRequests, yearStart, yearEnd]
  );

  const activeEmployees = useMemo(() => {
    return scopedEmployees.filter((el) => {
      if (!el.userSub || el.employeeStatus !== "Aktiv") return false;
      if (employeeOnly) return true;
      return yearLeaveSubs.has(el.userSub);
    });
  }, [scopedEmployees, yearLeaveSubs, employeeOnly]);

  const teams = useMemo(() => {
    const set = new Set();
    activeEmployees.forEach((e) => {
      if (e.employeeTeam) set.add(e.employeeTeam);
    });
    return [...set].sort((a, b) => a.localeCompare(b));
  }, [activeEmployees]);

  const filteredEmployees = useMemo(() => {
    if (employeeOnly) return activeEmployees;

    const q = (employeeSearch || "").trim().toLowerCase();
    return activeEmployees
      .filter((e) => {
        if (department && e.employeeDepartmentName !== department) return false;
        if (team && e.employeeTeam !== team) return false;
        if (q) {
          const name = `${e.employeeFirstName || ""} ${e.employeeLastName || ""}`
            .toLowerCase()
            .trim();
          if (!name.includes(q)) return false;
        }
        return true;
      })
      .sort((a, b) =>
        `${a.employeeFirstName || ""} ${a.employeeLastName || ""}`
          .trim()
          .localeCompare(
            `${b.employeeFirstName || ""} ${b.employeeLastName || ""}`.trim(),
            "sq",
            { sensitivity: "base" }
          )
      );
  }, [activeEmployees, department, team, employeeSearch, employeeOnly]);

  const filteredSubs = useMemo(
    () => new Set(filteredEmployees.map((e) => e.userSub).filter(Boolean)),
    [filteredEmployees]
  );

  // Bars / list / calendar: leave that overlaps the selected month.
  const monthLeave = useMemo(() => {
    const inMonth = filterLeaveByRange(
      employeeRequests || [],
      rangeStart,
      rangeEnd
    );
    return inMonth.filter((r) => filteredSubs.has(r.userSub));
  }, [employeeRequests, rangeStart, rangeEnd, filteredSubs]);

  const groups = useMemo(
    () =>
      filteredEmployees.map((n) => ({
        id: n.userSub,
        title: `${n.employeeFirstName || ""} ${n.employeeLastName || ""}`.trim(),
      })),
    [filteredEmployees]
  );

  return (
    <div className={`vacation-planer ${isDarkMode ? "is-dark" : "is-light"}`}>
      <header className="planer-header">
        <div>
          <h1>Planifikuesi i pushimeve</h1>
          <p className="planer-sub">
            {employeeOnly
              ? `Shikoni lejet tuaja për ${selectedYear}. Përdorni filtrin e muajit — pa veprime administruese.`
              : `Vetëm punonjësit me kërkesa leje në ${selectedYear}. Filtri i muajit afishon periudhat e atij muaji.`}
          </p>
        </div>
        <Space wrap>
          <Link to="/kryefaqja">
            <Button type="text">← Kryefaqja</Button>
          </Link>
          {canManage ? (
            <Button
              icon={<PlusOutlined />}
              onClick={() => setRequestModal(true)}
            >
              Kërkesë e re
            </Button>
          ) : (
            <Tag color="blue">Vetëm leximi</Tag>
          )}
          {canUseGmailInbox ? (
            <Button
              type="primary"
              className="planer-cta"
              icon={<MailOutlined />}
              onClick={() => setInboxOpen(true)}
            >
              Inbox email
            </Button>
          ) : null}
        </Space>
      </header>

      <div className="planer-toolbar">
        <PlanerMonthFilter month={month} onMonthChange={setMonth} />
        {!employeeOnly ? (
          <Segmented
            value={view}
            onChange={setView}
            options={[
              {
                label: "Gantt",
                value: VIEWS.gantt,
                icon: <BarChartOutlined />,
              },
              {
                label: "Listë",
                value: VIEWS.list,
                icon: <UnorderedListOutlined />,
              },
              {
                label: "Kalendar",
                value: VIEWS.calendar,
                icon: <CalendarOutlined />,
              },
            ]}
          />
        ) : null}
        <div className="planer-legend">
          {Object.entries(STATUS_COLORS).map(([status, color]) => (
            <Tag
              key={status}
              color={color}
              style={{ color: "#fff", border: "none" }}
            >
              {status}
            </Tag>
          ))}
        </div>
      </div>

      {canManage ? (
        <PlanerFilters
          departments={departments || []}
          teams={teams}
          department={department}
          team={team}
          employeeSearch={employeeSearch}
          onDepartmentChange={setDepartment}
          onTeamChange={setTeam}
          onEmployeeSearchChange={setEmployeeSearch}
        />
      ) : null}

      <div
        className={`planer-body view-${employeeOnly ? VIEWS.calendar : view}`}
        data-view={employeeOnly ? VIEWS.calendar : view}
      >
        {!employeeOnly && view !== VIEWS.calendar ? (
          <aside className="planer-aside">
            <PlanerOnLeaveSidebar
              employeesData={filteredEmployees}
              leave={employeeRequests || []}
            />
          </aside>
        ) : null}

        <main className="planer-main">
          {employeeOnly || view === VIEWS.calendar ? (
            <PlanerCalendarView
              month={month}
              leave={monthLeave}
              employees={filteredEmployees}
              onMonthChange={setMonth}
              onSelectRequest={(req) => setSelected(req)}
            />
          ) : null}
          {!employeeOnly && view === VIEWS.gantt ? (
            <PlanerGantt
              groups={groups}
              leave={monthLeave}
              rangeStart={rangeStart}
              rangeEnd={rangeEnd}
              onItemClick={(req) => setSelected(req)}
            />
          ) : null}
          {!employeeOnly && view === VIEWS.list ? (
            <PlanerListView
              leave={monthLeave}
              employees={filteredEmployees}
              onRowClick={(req) => setSelected(req)}
            />
          ) : null}
        </main>
      </div>

      <PlanerDrawer
        open={!!selected}
        content={selected}
        onClose={() => setSelected(null)}
        canManage={canManage}
      />

      {canUseGmailInbox ? (
        <InboxQueuePanel open={inboxOpen} onClose={() => setInboxOpen(false)} />
      ) : null}

      {canManage && requestModal ? (
        <RequestModal
          open={requestModal}
          setOpen={setRequestModal}
          request={editRequest}
          setRequest={setEditRequest}
        />
      ) : null}
    </div>
  );
};

export default PlanerPage;
